import { Checkbox, FormInput, FormSelect, Hint, Popover, toast } from '@systemeio/ui';
import FormSenderEmailInput from 'modules/email/components/form-sender-email-input';
import SettingsLayout from 'modules/profile/components/settings-layout';
import SettingsSectionHeading from 'modules/profile/components/settings-section-heading';
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys';
import { ContactsCleaningSettings } from 'modules/profile/mailing-settings/components/contacts-cleaning-settings';
import DomainTable from 'modules/profile/mailing-settings/components/domain-table';
import DoubleOptInModal from 'modules/profile/mailing-settings/components/double-opt-in-modal';
import { ErrorMessagesForEmailFooter } from 'modules/profile/mailing-settings/components/error-messages-for-email-footer';
import MailingSettingsSkeleton from 'modules/profile/mailing-settings/components/mailing-settings-skeleton';
import SenderEmailAddresses from 'modules/profile/mailing-settings/components/sender-emails-addresses';
import UnsubscribeLink from 'modules/profile/mailing-settings/components/unsubscribe-link';
import { defaultErrors } from 'modules/profile/mailing-settings/constants/default-errors';
import { defaultValues } from 'modules/profile/mailing-settings/constants/default-values';
import { MailingSettingsLoco } from 'modules/profile/mailing-settings/constants/mailing-settings-loco';
import { useMailingSettings } from 'modules/profile/mailing-settings/hooks/use-mailing-settings';
import { useRemovalConditions } from 'modules/profile/mailing-settings/hooks/use-removal-conditions';
import { useRemovalConditionsUpdate } from 'modules/profile/mailing-settings/hooks/use-update-contacts-cleaning';
import { useUpdateEmailFooter } from 'modules/profile/mailing-settings/hooks/use-update-email-footer';
import { useUpdateMailingSettings } from 'modules/profile/mailing-settings/hooks/use-update-mailing-settings';
import { MailingSettingsEditInterface, MailingSettingsErrorsInterface } from 'modules/profile/mailing-settings/types/mailing-settings-interface';
import { RemovalConditionsInterface } from 'modules/profile/mailing-settings/types/removal-conditions-interface';
import { NextPage } from 'next';
import React, { useEffect, useMemo, useState } from 'react';
import { useEmailFooterData } from 'shared/components/dashboard-mail-editor/hooks/use-email-footer-data';
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options';
import { EmailFooterDataInterface } from 'shared/components/dashboard-mail-editor/types/email-footer-interface';
import FormTextarea from 'shared/components/form-textarea';
import { SectionMessage } from 'shared/components/section-message';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useScrollToElement from 'shared/hooks/use-scroll-to-element';
import useUser from 'shared/hooks/use-user';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { QuestionMarkIcon } from '@systemeio/ui/icons';
import { SenderEmailInput } from 'modules/profile/mailing-settings/components/sender-email-input';
import { useNormalizeSenderEmail } from 'modules/profile/mailing-settings/hooks/use-normalize-sender-email';
const MailingSettings: NextPage = () => {
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    isWsAssistant
  } = useUser();
  const {
    mutate: mutateOptions
  } = useEmailOptions();
  const {
    removalConditions,
    mutate: mutateRemovalConditions,
    canManageRemovalConditions
  } = useRemovalConditions();
  const {
    removalConditionsUpdate
  } = useRemovalConditionsUpdate();
  const [tempState, setTempState] = useState<MailingSettingsEditInterface>(defaultValues);
  const [tempStateEmailFooter, setTempStateEmailFooter] = useState<EmailFooterDataInterface[]>([]);
  const [selectedEmailFooterDomainName, setSelectedEmailFooterDomainName] = useState<string | undefined>();
  const [tempStateRemovalConditions, setTempStateRemovalConditions] = useState<RemovalConditionsInterface[]>([]);
  const [isOwnSendgrid, setIsOwnSendgrid] = useState(false);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [errors, setErrors] = useState<MailingSettingsErrorsInterface>(defaultErrors);
  const [isFetching, setIsFetching] = useState(false);
  const {
    mailingData,
    mutate
  } = useMailingSettings();
  const {
    emailFooterData,
    isFetchingEmailFooterData,
    lastUpdatedFooter,
    mutateEmailFooterData
  } = useEmailFooterData();
  const {
    fetcher: updateEmailFooter
  } = useUpdateEmailFooter();
  const {
    fetcher: updateMailingSettings
  } = useUpdateMailingSettings();
  const emailFooterOptions = useMemo(() => emailFooterData?.items.map(({
    domainName
  }) => ({
    id: domainName,
    caption: domainName
  })), [emailFooterData]);
  const {
    isReadyToNormalize,
    normalizeSenderEmail
  } = useNormalizeSenderEmail();
  const onSubmit = async () => {
    try {
      setErrors(defaultErrors);
      setIsFetching(true);
      if (isOwnSendgrid && !tempState.sendgridApiKey) {
        setErrors(prev => ({
          ...prev,
          sendgridApiKey: t('dashboard.validation.empty_error.title')
        }));
        setIsFetching(false);
        return;
      }
      const data: MailingSettingsEditInterface = {
        ...tempState,
        senderEmailAddress: normalizeSenderEmail(tempState.senderEmailAddress)
      };
      const mailingResponse = await updateMailingSettings(data, errors, setErrors);
      await mutate(mailingResponse, false);
      if (emailFooterData && tempStateEmailFooter) {
        const footerResponse = await updateEmailFooter({
          items: tempStateEmailFooter,
          hasMore: emailFooterData.hasMore
        }, setErrors);
        await mutateEmailFooterData(footerResponse, false);
      }
      if (canManageRemovalConditions) {
        const newDataRemovalConditions = tempStateRemovalConditions.map(item => ({
          active: item.active,
          type: item.type,
          action: item.action,
          dateRange: item.dateRange
        }));
        const removalResponse = await removalConditionsUpdate({
          listCleanings: newDataRemovalConditions
        });
        await mutateRemovalConditions(removalResponse, false);
      }
      await mutateOptions();
      toast.success(t('global.changes_saved'));
      setErrors(defaultErrors);
      setIsFetching(false);
    } catch (e) {
      setIsFetching(false);
    }
  };
  useEffect(() => {
    if (mailingData) {
      setTempState(prev_0 => ({
        ...prev_0,
        sendgridApiKey: mailingData.sendgridApiKey,
        affiliateEmailLink: mailingData.affiliateEmailLink,
        senderEmailAddress: mailingData.senderEmailAddress,
        senderEmailName: mailingData.senderEmailName,
        testEmailAddress: mailingData.testEmailAddress,
        emailMessageFooter: mailingData.emailMessageFooter,
        unsubscriptionLinkText: mailingData.unsubscriptionLinkText,
        displayUnsubscribeFromCampaignLink: mailingData.displayUnsubscribeFromCampaignLink
      }));
      if (mailingData.sendgridApiKey) {
        setIsOwnSendgrid(true);
      }
    }
    if (removalConditions) {
      setTempStateRemovalConditions(removalConditions);
    }
  }, [mailingData, removalConditions]);
  useScrollToElement(globalThis.location?.hash);
  useEffect(() => {
    if (lastUpdatedFooter) {
      if (lastUpdatedFooter.domainName) {
        setSelectedEmailFooterDomainName(lastUpdatedFooter.domainName);
      }
    }
  }, [emailFooterData, lastUpdatedFooter]);
  useEffect(() => {
    if (emailFooterData) {
      setTempStateEmailFooter(emailFooterData.items);
    }
  }, [emailFooterData]);
  return <SettingsLayout titles={[{
    caption: t(settingsMenuLocoKeys.mailing),
    isStatic: true
  }]} onSave={onSubmit} disabled={!mailingData || !isReadyToNormalize} isFetching={isFetching}>
      {mailingData && user ? <div className="bg-background-secondary [&>*]:flex-1 flex h-fit w-full flex-col gap-7 overflow-x-hidden rounded-md p-5 lg:gap-10">
          <div className="flex flex-col gap-7 lg:gap-10">
            <div className="flex flex-col gap-4">
              <SettingsSectionHeading>
                {t('dashboard.settings.mailing.section_heading.send_grid')}
              </SettingsSectionHeading>
              <Checkbox value={isOwnSendgrid} label={<>
                    <div className={`flex items-center gap-3 ${tempState.sendgridApiKey && '[&>svg]:fill-brand-primary [&>svg]:main-transition-colors'}`}>
                      {t('dashboard.settings.mailing.sengrid_api_key_title')}
                      <Popover label={<div className="[&>a]:text-brand-primary" dangerouslySetInnerHTML={{
                __html: t('dashboard.settings.mailing.sengrid_api_key_popover_title')
              }} />} popoverClassName={'w-[200px] sm:w-[350px]'}>
                        {show => <QuestionMarkIcon className={`${show ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                      </Popover>
                    </div>
                  </>} onChange={() => {
            if (isOwnSendgrid) {
              setTempState(prev_1 => ({
                ...prev_1,
                sendgridApiKey: null
              }));
            }
            setIsOwnSendgrid(prev_2 => !prev_2);
          }} />
              {isOwnSendgrid && <FormInput value={tempState.sendgridApiKey || ''} onChange={e_0 => {
            setErrors(prev_3 => ({
              ...prev_3,
              sendgridApiKey: ''
            }));
            setTempState(prev_4 => ({
              ...prev_4,
              sendgridApiKey: e_0.target.value
            }));
          }} error={errors.sendgridApiKey} />}
            </div>
            {!isOwnSendgrid && <div>
                <SettingsSectionHeading className="mb-4">
                  {t('dashboard.settings.mailing.domains.table_title')}
                </SettingsSectionHeading>
                <DomainTable />
              </div>}
            <div id="verified-email-addresses">
              <SettingsSectionHeading className="mb-4">
                {t('dashboard.settings.mailing.sender_email_addresses.table_title')}
                <Popover label={t('dashboard.settings.mailing.sender_email_address_popover_title')} popoverClassName={'w-[200px] sm:w-[350px]'} wrapperClassName={'inline-block'}>
                  {show_0 => <QuestionMarkIcon className={`ml-1.5 ${show_0 ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                </Popover>
              </SettingsSectionHeading>
              {showEmailWarning && <SectionMessage className="mb-4">
                  {t('dashboard.email.new_google_policy_warning_message')}
                </SectionMessage>}
              <SenderEmailAddresses setShowEmailWarning={setShowEmailWarning} />
            </div>
            <div id="sender-email-address">
              <SettingsSectionHeading className="mb-4">
                {t('dashboard.settings.mailing.section_heading.sender')}
              </SettingsSectionHeading>
              <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
                <SenderEmailInput value={tempState.senderEmailAddress || ''} onChange={value => setTempState(prev_5 => ({
              ...prev_5,
              senderEmailAddress: value
            }))} error={errors.senderEmailAddress} disabled={isWsAssistant} />
                <FormInput placeholder={t('dashboard.settings.mailing.sender_name_title')} labelClassName={'text-text-primary'} label={<div className={'flex items-center gap-1.5'}>
                      {t('dashboard.settings.mailing.sender_name_title')}
                      <Popover label={t('dashboard.settings.mailing.sender_name_popover_title')} popoverClassName={'w-[200px] sm:w-[350px]'}>
                        {show_1 => <QuestionMarkIcon className={`${show_1 ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                      </Popover>
                    </div>} value={tempState.senderEmailName || ''} onChange={e_1 => setTempState(prev_6 => ({
              ...prev_6,
              senderEmailName: e_1.target.value
            }))} error={errors.senderEmailName} />
              </div>
            </div>
            <div className={'flex flex-col gap-4'}>
              <SettingsSectionHeading>
                {t('dashboard.settings.mailing.section_heading.testing')}
              </SettingsSectionHeading>
              <FormSenderEmailInput labelClassName={'text-text-primary'} label={<div className={'flex items-center gap-1.5'}>
                    {t('dashboard.settings.mailing.test_email_address_title')}
                    <Popover label={t('dashboard.settings.mailing.test_email_address_popover_title')} popoverClassName={'w-[200px] sm:w-[350px]'}>
                      {show_2 => <QuestionMarkIcon className={`${show_2 ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                    </Popover>
                  </div>} required value={tempState.testEmailAddress || ''} onChange={e_2 => setTempState(prev_7 => ({
            ...prev_7,
            testEmailAddress: e_2.target.value
          }))} error={errors.testEmailAddress} />
            </div>
            <div>
              <SettingsSectionHeading className={'mb-4'}>
                <div className="flex items-center">
                  {t('dashboard.settings.mailing.double_opt_in_title')}
                  <Popover label={t('dashboard.settings.mailing.double_opt_in_popover_title')} popoverClassName={'w-[200px] sm:w-[350px]'}>
                    {show_3 => <QuestionMarkIcon className={`ml-1.5 ${show_3 ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                  </Popover>
                </div>
              </SettingsSectionHeading>
              <div className="w-full">
                <DoubleOptInModal />
              </div>
            </div>

            {removalConditions && canManageRemovalConditions && <div className={'flex flex-col gap-4'}>
                <SettingsSectionHeading>
                  <div className={'mb-1 flex items-center gap-1.5'}>
                    {t('dashboard.settings.mailing.contacts_cleaning_title')}
                    <Popover label={t('dashboard.settings.mailing.contacts_cleaning_popover_text')} popoverClassName={'w-[200px] sm:w-[350px]'}>
                      {show_4 => <QuestionMarkIcon className={`${show_4 ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                    </Popover>
                  </div>
                </SettingsSectionHeading>
                <div className={'flex flex-col gap-4'}>
                  {tempStateRemovalConditions.map((item_0, index) => <ContactsCleaningSettings key={`${item_0.type}-${index}`} removalConditions={item_0} setTempStateRemovalConditions={setTempStateRemovalConditions} />)}
                </div>
              </div>}
            <div className={'flex flex-col gap-4'}>
              <SettingsSectionHeading>
                {t('dashboard.settings.mailing.section_heading.email_footer')}
              </SettingsSectionHeading>

              <Checkbox value={tempState.affiliateEmailLink} label={t('dashboard.settings.mailing.affiliate_email_link_title')} onChange={() => setTempState(prev_8 => {
            return {
              ...prev_8,
              affiliateEmailLink: !prev_8.affiliateEmailLink
            };
          })} disabled={!mailingData.canEditShowEmailAffiliateLink} />

              <Checkbox value={tempState.displayUnsubscribeFromCampaignLink} label={<Hint label={t('dashboard.settings.mailing.display_unsubscribe_from_campaign_link_popover_title')}>
                    {t('dashboard.settings.mailing.display_unsubscribe_from_campaign_link')}
                  </Hint>} onChange={() => setTempState(prev_9 => {
            return {
              ...prev_9,
              displayUnsubscribeFromCampaignLink: !prev_9.displayUnsubscribeFromCampaignLink
            };
          })} />

              <UnsubscribeLink tempState={tempState} setTempState={setTempState} locale={user.dashboardLocale} errors={errors.unsubscriptionLinkText} />

              <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-2'}>
                  <Hint label={t('dashboard.settings.mailing.email_message_footer_title')}>
                    {t('dashboard.settings.mailing.email_message_footer_popover_title')}
                  </Hint>
                  <FormSelect isPreFetching={isFetchingEmailFooterData} data={emailFooterOptions} value={selectedEmailFooterDomainName} onChange={data_0 => setSelectedEmailFooterDomainName(data_0)} placeholder={t('dashboard.settings.mailing.placeholder_select_domain')} />
                </div>

                <ErrorMessagesForEmailFooter errors={errors} emailFooterOptions={emailFooterOptions} />

                <FormTextarea value={tempStateEmailFooter.find(item_1 => item_1.domainName === selectedEmailFooterDomainName)?.content || ''} className={'min-h-[170px]'} disabled={!selectedEmailFooterDomainName} onChange={e_3 => setTempStateEmailFooter(prevState => prevState.map(item_2 => item_2.domainName === selectedEmailFooterDomainName ? {
              ...item_2,
              content: e_3.target.value
            } : item_2))} />
              </div>
            </div>
            {errors.common && <>
                {errors.common.map((error, index_0) => <p className="text-red px-6 text-sm" key={index_0}>
                    {error}
                  </p>)}
              </>}
          </div>
        </div> : <MailingSettingsSkeleton />}
    </SettingsLayout>;
};
export const getServerSideProps = getBaseServerSideProps(MailingSettingsLoco, 'global.settings');
export default MailingSettings;